<template>
  <div class="yoyo-container mx-auto min-h-screen">
    <!-- 导航栏 -->
    <navBar :title="$t('reviews.title')"></navBar>

    <!-- 评价统计卡片 -->
    <div class="bg-white mx-4 mt-4 rounded-xl p-4 pb-3 text-center">
      <div class="flex items-center justify-center">
        <h3 class="text-lg font-medium text-gray-800 mb-1 mr-3">{{ $t("reviews.totalReviews") }}</h3>
        <!-- 评价数量显示 -->
        <div class="flex items-center justify-center mb-1 text-2xl font-bold text-[#ff7a00]">
          {{ totalReviewsCount }}
        </div>
      </div>
      <!-- yoyo币统计 -->
      <div class="text-sm text-gray-500" v-if="$i18n.locale === 'zh-CN'">
        ({{ $t("reviews.totalYoyoCount") }}{{ totalYoyoCount }}{{ $t("mine.points") }})
      </div>
      <div class="text-sm text-gray-500" v-else>({{ totalYoyoCount }} {{ $t("reviews.totalYoyoCount") }})</div>
    </div>

    <!-- 标签页 -->
    <div class="mx-4 mt-4 bg-white rounded-xl overflow-hidden">
      <van-tabs v-model="activeTab" class="review-tabs yoyo-container" sticky offset-top="3rem">
        <van-tab :title="$t('reviews.tabs.pending')" name="pending">
          <!-- 待评价记录 -->
          <van-pull-refresh v-model="pendingRefreshing" @refresh="onRefreshPending" v-if="pendingReviews.length > 0">
            <van-list
              v-model="pendingLoading"
              :finished="pendingFinished"
              :finished-text="$t('query.noMoreData')"
              :loading-text="$t('query.loading')"
              @load="onLoadPending">
              <div class="px-4">
                <div v-for="(review, index) in pendingReviews" :key="index" class="py-4 border-b border-gray-100 last:border-b-0">
                  <!-- 订单号和日期 -->
                  <div class="flex justify-between items-center mb-3 text-sm">
                    <span class="text-gray-600 text-xs">{{ $t("reviews.orderNumber") }}: {{ review.itemNo }}</span>
                    <span class="text-gray-400 text-xs">{{ formatDate(review.createTime) }}</span>
                  </div>
                  <!-- 产品信息 -->
                  <div class="flex gap-3">
                    <!-- 产品图片 -->
                    <img :src="imageBaseUrl + review.picUrl" alt="" class="w-12 h-12 rounded-lg object-cover bg-gray-100" />
                    <!-- 产品详情 -->
                    <div class="flex-1 flex flex-col justify-between">
                      <div>
                        <div class="text-sm font-medium text-gray-800 mb-1">
                          {{ review.spuType }}-{{ isZh ? review.spuPlace : review.spuPlaceEn }}
                        </div>
                        <div class="text-sm text-gray-600">
                          {{ isZh ? review.packageValue?.name : review.packageValue?.nameEn }}
                          {{ isZh ? review.dayValue?.name + $t("products.day") : review.dayValue?.nameEn + $t("products.day") }}
                        </div>
                      </div>
                    </div>
                    <!-- 评价按钮 -->
                    <div class="flex-shrink-0">
                      <van-button
                        type="primary"
                        size="small"
                        class="w-16 h-8 leading-10 p-0 rounded-lg bg-orange-500 border-0"
                        @click="handleReview(review)">
                        {{ $t("reviews.reviewButton") }}
                      </van-button>
                    </div>
                  </div>
                </div>
              </div>
            </van-list>
          </van-pull-refresh>
          <van-empty v-if="pendingReviews.length === 0" :description="$t('common.noData')" />
        </van-tab>

        <van-tab :title="$t('reviews.tabs.completed')" name="completed">
          <!-- 已评价记录 -->
          <van-pull-refresh v-model="completedRefreshing" @refresh="onRefreshCompleted" v-if="completedReviews.length > 0">
            <van-list
              v-model="completedLoading"
              :finished="completedFinished"
              :finished-text="$t('query.noMoreData')"
              :loading-text="$t('query.loading')"
              @load="onLoadCompleted">
              <div class="px-4">
                <div v-for="(review, index) in completedReviews" :key="index" class="py-4 border-b border-gray-100 last:border-b-0">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <div class="font-medium  text-xs text-gray-800 mb-1">{{ review.productName }}</div>
                      <div class="text-sm text-gray-500 mb-1">{{ $t("reviews.orderNumber") }}: {{ review.itemNo }}</div>
                      <div class="text-sm text-gray-400 mb-2">{{ review.orderDate }}</div>
                      <!-- 产品信息 -->
                      <div class="flex gap-3">
                        <!-- 产品图片 -->
                        <img :src="imageBaseUrl + review.picUrl" alt="" class="w-12 h-12 rounded-lg object-cover bg-gray-100" />
                        <!-- 产品详情 -->
                        <div class="flex-1 flex flex-col justify-between">
                          <div>
                            <div class="font-medium text-gray-800 mb-1 text-sm">
                              {{ review.spuType }}-{{ isZh ? review.spuPlace : review.spuPlaceEn }}
                            </div>
                            <div class="text-sm text-gray-600">
                              {{ isZh ? review.packageValue?.name : review.packageValue?.nameEn }}
                              {{ isZh ? review.dayValue?.name + $t("products.day") : review.dayValue?.nameEn + $t("products.day") }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- 评价信息 -->
                      <div class="bg-gray-50 rounded-lg p-3 mt-2">
                        <div class="flex items-center mb-2">
                          <van-rate v-model="review.scores" :readonly="true" :size="14" color="#ffd21e" void-color="#eee" />
                          <span class="text-sm text-gray-600 ml-2">{{ review.scores }} {{ $t("reviews.stars") }}</span>
                        </div>
                        <div class="text-sm text-gray-700 mb-1">{{ review.content }}</div>
                        <div class="text-xs text-gray-400">{{ $t("reviews.reviewTime") }}: {{ formatDate(review.createTime) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </van-list>
          </van-pull-refresh>
          <van-empty v-if="completedReviews.length === 0" :description="$t('common.noData')" />
        </van-tab>
      </van-tabs>
    </div>

    <!-- 评价弹框 -->
    <CommonDialog :visible.sync="showReviewPopup" :buttons="dialogButtons" @button-click="handleButtonClick" @close="handleClose">
      <div v-if="currentReview" class="">
        <!-- 星级评分 -->
        <div class="mb-6 text-center">
          <van-rate v-model="reviewForm.scores" :size="34" color="#ffd21e" void-color="#eee" class="justify-center" />
        </div>

        <!-- 评价标签 -->
        <div class="flex justify-center items-center flex-wrap gap-2">
          <div
            v-for="tag in reviewTags"
            :key="tag.id"
            :class="[
              'py-1 px-2 rounded-full border cursor-pointer transition-colors text-sm',
              tag.checked ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-300 text-gray-700',
            ]"
            @click="toggleTag(tag)">
            <span class="flex items-center justify-center">{{ isZh ? tag.name : tag.nameEn }}</span>
          </div>
        </div>

        <!-- 评价内容输入框 -->
        <div class="my-4">
          <van-field
            v-model="reviewForm.content"
            type="textarea"
            :placeholder="$t('reviews.reviewPlaceholder')"
            rows="3"
            maxlength="500"
            show-word-limit
            :border="false"
            class="bg-gray-50 rounded-lg" />
        </div>
      </div>
    </CommonDialog>
  </div>
</template>

<script>
import { Tabs, Tab, Field, Rate, PullRefresh, List, Empty } from "vant"
import { getCommentPage, getCommentTagList, addComment } from "@/apis/mine"
import dayjs from "dayjs"
import { mapGetters } from "vuex"
export default {
  name: "MyReviews",
  components: {
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
    [Field.name]: Field,
    [Rate.name]: Rate,
    [PullRefresh.name]: PullRefresh,
    [List.name]: List,
    [Empty.name]: Empty,
  },
  data() {
    return {
      activeTab: "pending",
      showReviewPopup: false,
      submitting: false,
      currentReview: null,
      reviewForm: {
        scores: 5,
        content: "",
      },
      reviewTags: [],
      // 待评价分页
      pendingReviews: [],
      pendingLoading: false,
      pendingFinished: false,
      pendingRefreshing: false,
      pendingPageNo: 1,
      pendingPageSize: 10,
      // 已评价分页
      completedReviews: [],
      completedLoading: false,
      completedFinished: false,
      completedRefreshing: false,
      completedPageNo: 1,
      completedPageSize: 10,
      // 统计信息
      totalReviewsCount: 0,
      totalYoyoCount: 0,
      dialogButtons: [{ text: this.$t("reviews.publish"), type: "primary", action: "submit" }],
    }
  },
  computed: {
    ...mapGetters({
      isZh: "lang/isZh",
    }),

    imageBaseUrl() {
      return process.env.VUE_APP_IMAGE_URL
    },
  },
  watch: {
    activeTab() {
      this.onTabChange()
    },
  },
  methods: {
    // 格式化时间戳
    formatDate(timestamp) {
      return dayjs(timestamp).format("YYYY-MM-DD")
    },
    // 标签页切换
    onTabChange() {
      if (this.activeTab === "pending" && this.pendingReviews.length === 0) {
        this.getCommentList(true)
      } else if (this.activeTab === "completed" && this.completedReviews.length === 0) {
        this.getCommentList(false)
      }
    },
    // 待评价 - 下拉刷新
    onRefreshPending() {
      this.pendingPageNo = 1
      this.pendingFinished = false
      this.pendingReviews = []
      this.getCommentList(true)
    },
    // 待评价 - 上拉加载
    onLoadPending() {
      this.getCommentList(true)
    },
    // 已评价 - 下拉刷新
    onRefreshCompleted() {
      this.completedPageNo = 1
      this.completedFinished = false
      this.completedReviews = []
      this.getCommentList(false)
    },
    // 已评价 - 上拉加载
    onLoadCompleted() {
      this.getCommentList(false)
    },
    // 获取评价列表
    async getCommentList(isPending = true) {
      try {
        const params = {
          pageNo: isPending ? this.pendingPageNo : this.completedPageNo,
          pageSize: isPending ? this.pendingPageSize : this.completedPageSize,
          commentStatus: !isPending, // pending时false，completed时true
        }
        const res = await getCommentPage(params)
        const list = res.data.list || []

        if (isPending) {
          // 待评价记录
          if (this.pendingPageNo === 1) {
            this.pendingReviews = list
            this.pendingRefreshing = false
          } else {
            this.pendingReviews = [...this.pendingReviews, ...list]
          }
          this.pendingLoading = false

          // 判断是否加载完
          if (res.data.total <= this.pendingReviews.length) {
            this.pendingFinished = true
          } else {
            this.pendingPageNo++
          }
        } else {
          // 已评价记录
          if (this.completedPageNo === 1) {
            this.completedReviews = list
            this.completedRefreshing = false
          } else {
            this.completedReviews = [...this.completedReviews, ...list]
          }
          this.completedLoading = false

          // 判断是否加载完
          if (res.data.total <= this.completedReviews.length) {
            this.completedFinished = true
          } else {
            this.completedPageNo++
          }
        }

        // 更新总数
        const extra = res.data.extra || {}
        this.totalReviewsCount = extra.commentCount
        this.totalYoyoCount = extra.goodCommentPointTotal

        console.log("评价列表:", res)
      } catch (error) {
        console.log("获取评价列表失败:", error)
        if (isPending) {
          this.pendingLoading = false
          this.pendingRefreshing = false
        } else {
          this.completedLoading = false
          this.completedRefreshing = false
        }
      }
    },
    handleReview(review) {
      this.currentReview = review
      this.reviewForm = {
        scores: 5,
        content: "",
      }
      // 重置所有标签为未选中状态
      this.reviewTags.forEach((tag) => {
        this.$set(tag, "checked", false)
      })
      this.showReviewPopup = true
    },
    toggleTag(tag) {
      this.$set(tag, "checked", !tag.checked)
    },
    handleButtonClick(event) {
      if (event.action === "submit") {
        if(this.reviewForm.content.length <5) {
          this.$toast(this.$t("reviews.reviewMinLength"))
          return
        }
        this.submitReview()
      }
    },
    handleClose() {
      this.showReviewPopup = false
    },
    async submitReview() {
      try {
        const tagList = this.reviewTags.filter((tag) => tag.checked).map((tag) => tag.name)
        const tagListEn = this.reviewTags.filter((tag) => tag.checked).map((tag) => tag.nameEn)
        const res = await addComment({
          orderItemId: this.currentReview.id,
          scores: this.reviewForm.scores,
          content: this.reviewForm.content,
          tagList,
          tagListEn,
        })
        this.onRefreshPending()
        this.showReviewPopup = false
      } catch (error) {
        console.log("error :>> ", error)
      }
    },
    async getCommentTagList() {
      try {
        const res = await getCommentTagList()
        this.reviewTags = res.data
      } catch (error) {}
    },
  },
  created() {
    this.getCommentList(true)
    this.getCommentTagList()
  },
}
</script>

<style scoped>
/deep/ .van-sticky {
  width: 100%;
}

@media (width >= 40rem) {
  :deep(.van-sticky) {
    max-width: 40rem;
    margin-inline: auto;
    padding: 0 1rem;
  }
}

@media (width >= 48rem) {
  :deep(.van-sticky) {
    max-width: 48rem;
    margin-inline: auto;
    padding: 0 1rem;
  }
}

@media (width >= 64rem) {
  :deep(.van-sticky) {
    max-width: 64rem;
    margin-inline: auto;
    padding: 0 1rem;
  }
}
@media (width >= 80rem) {
  :deep(.van-sticky) {
    max-width: 64rem;
    margin-inline: auto;
    padding: 0 1rem;
  }
}
@media (width >= 96rem) {
  :deep(.van-sticky) {
    max-width: 72rem;
    margin-inline: auto;
    padding: 0 1rem;
  }
}
</style>
